import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, StyleSheet, View } from 'react-native';
import { useRecoilValue } from 'recoil';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useShowNextLDLProduct } from '@hooks';
import { dayDateTimeState, hourlyForecastState } from '@store';

import { formatPrecipChance, formatTemperature } from '../../../helpers/DataFormatting';
import { isAndroid } from '../../../helpers/Platform';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCAnimatedIcon } from '../../primitives/TWCAnimatedIcon/TWCAnimatedIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const testIDPath = lowerDisplayLineTestIDs.hourly;
const tPath = 'lowerDisplayLine.hourly.';

const PRODUCT_PAGES = 3;
const HOURS_DISPLAYED = 4;

const getFilteredData = data => {
  if (!data || Object.keys(data).length === 0) {
    return;
  }

  const { iconCodeExtend, precipChance, temperature, validTimeUtc } = data;

  return Array.from({ length: PRODUCT_PAGES }, (_, index) => {
    const sliceStart = HOURS_DISPLAYED * index;
    const sliceEnd = sliceStart + HOURS_DISPLAYED;

    return {
      iconCodeExtend: iconCodeExtend.slice(sliceStart, sliceEnd),
      temperature: temperature.slice(sliceStart, sliceEnd),
      precipChance: precipChance.slice(sliceStart, sliceEnd),
      validTimeUtc: validTimeUtc.slice(sliceStart, sliceEnd),
    };
  });
};

const getFormattedPages = (hourlyDataPages, hours, timeZone) => {
  if (!hourlyDataPages) {
    return null;
  }

  const finalDataObject = {};

  for (const [index, page] of hourlyDataPages.entries()) {
    const { iconCodeExtend, temperature, precipChance, validTimeUtc } = page;
    const { dateFromEpoch, getZonedHourTime } = TimeUtils;

    const newPage = Array.from({ length: hours }, (_, hourIndex) => ({
      timeLabel: getZonedHourTime(dateFromEpoch(validTimeUtc[hourIndex]), timeZone, '12h'),
      iconCodeExtend: iconCodeExtend[hourIndex],
      temperature: formatTemperature(temperature[hourIndex]),
      precipChance: formatPrecipChance(precipChance[hourIndex]),
    }));

    finalDataObject[`page${index + 1}`] = newPage;
  }

  return finalDataObject;
};

const LDLHourlyAPI = () => {
  const { data } = useRecoilValue(hourlyForecastState);
  const showNextProduct = useShowNextLDLProduct();

  useEffect(() => {
    !data && showNextProduct(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data) {
    return <></>;
  }

  return <LDLHourlyUI data={data} onFinish={showNextProduct} />;
};

const LDLHourlyUI = ({ data, onFinish }) => {
  const { t } = useTranslation();
  const { timeZone } = useRecoilValue(dayDateTimeState);

  const internalPageOneAnimation = useRef(new Animated.Value(Theme.px(2))).current;
  const internalPageTwoAnimation = useRef(new Animated.Value(Theme.px(2))).current;
  const internalPageThreeAnimation = useRef(new Animated.Value(Theme.px(2))).current;
  const finalShowValue = useRef(new Animated.Value(0)).current;

  const pageSlideAnimation = useRef(new Animated.Value(0)).current;

  const internalPageOneSlide = Animated.timing(internalPageOneAnimation, {
    delay: 6000,
    toValue: isAndroid ? Theme.px(-95) : Theme.px(-90),
    duration: 500,
    useNativeDriver: true,
  });

  const firstPageAnimation = Animated.timing(pageSlideAnimation, {
    delay: 12000,
    toValue: isAndroid ? Theme.px(-188) : Theme.px(-182),
    duration: 500,
    useNativeDriver: true,
  });

  const internalPageTwoSlide = Animated.timing(internalPageTwoAnimation, {
    delay: 18000,
    toValue: isAndroid ? Theme.px(-95) : Theme.px(-90),
    duration: 500,
    useNativeDriver: true,
  });

  const secondPageAnimation = Animated.timing(pageSlideAnimation, {
    delay: 24000,
    toValue: isAndroid ? Theme.px(-378) : Theme.px(-368),
    duration: 500,
    useNativeDriver: true,
  });

  const internalPageThreeSlide = Animated.timing(internalPageThreeAnimation, {
    delay: 30000,
    toValue: isAndroid ? Theme.px(-95) : Theme.px(-90),
    duration: 500,
    useNativeDriver: true,
  });

  const finalShow = Animated.timing(finalShowValue, {
    duration: 5000,
    useNativeDriver: true,
  });

  const hourlyDataPages = getFilteredData(data);
  const formattedData = getFormattedPages(hourlyDataPages, HOURS_DISPLAYED, timeZone);

  const animationSequence = formattedData
    ? [
        internalPageOneSlide,
        firstPageAnimation,
        internalPageTwoSlide,
        secondPageAnimation,
        internalPageThreeSlide,
        finalShow,
      ]
    : [];

  useEffect(() => {
    const animation = Animated.sequence([...animationSequence]);

    animation.start(({ finished }) => finished && onFinish?.());
    return animation.reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!formattedData) {
    return null;
  }

  const { page1, page2, page3 } = formattedData;

  return (
    <View>
      <TWCText
        style={styles.productText}
        testID={`${testIDPath.productLabel}_page_one`}
        variant={'condensedText36Transparent'}
      >
        {t(`${tPath}productLabel`)}
      </TWCText>
      <Animated.View
        style={[
          styles.pageWrapper,
          {
            transform: [{ translateY: pageSlideAnimation }],
          },
        ]}
        useNativeDriver
      >
        <View style={[styles.page, { transform: [{ translateY: Theme.px(2) }] }]}>
          {page1.map((hour, index) => {
            const { timeLabel, iconCodeExtend, temperature, precipChance } = hour;

            return (
              <View
                key={timeLabel}
                style={[
                  styles.page,
                  {
                    left: Theme.px(125),
                  },
                ]}
              >
                <TWCText
                  style={styles.hourText}
                  testID={`${testIDPath.productLabel}_page_one_hour_${index + 1}`}
                  variant={'condensedText36'}
                >
                  {timeLabel}
                </TWCText>
                <View style={styles.animatedIcon}>
                  <TWCAnimatedIcon code={iconCodeExtend} />
                </View>
                <View>
                  <Animated.View
                    style={{
                      transform: [{ translateY: internalPageOneAnimation }],
                    }}
                    useNativeDriver
                  >
                    <TWCText
                      style={styles.sectionEnd}
                      testID={`${testIDPath.temperature}_page_one_hour_${index + 1}`}
                      variant={'condensedText36'}
                    >
                      {temperature}
                    </TWCText>
                    <TWCText
                      style={
                        ([styles.sectionEnd],
                        {
                          transform: [{ translateY: Theme.px(50) }],
                        })
                      }
                      testID={`${testIDPath.precipitation}_page_one_hour_${index + 1}`}
                      variant={'condensedText36'}
                    >
                      {precipChance}
                    </TWCText>
                  </Animated.View>
                </View>
              </View>
            );
          })}
        </View>
        <View style={[styles.page, { transform: [{ translateY: Theme.px(100) }] }]}>
          {page2.map((hour, index) => {
            const { timeLabel, iconCodeExtend, temperature, precipChance } = hour;

            return (
              <View
                key={timeLabel}
                style={[
                  styles.page,
                  {
                    left: Theme.px(125),
                  },
                ]}
              >
                <TWCText
                  style={styles.hourText}
                  testID={`${testIDPath.productLabel}_page_two_hour_${index + 1}`}
                  variant={'condensedText36'}
                >
                  {timeLabel}
                </TWCText>
                <View style={styles.animatedIcon}>
                  <TWCAnimatedIcon code={iconCodeExtend} />
                </View>
                <View>
                  <Animated.View
                    style={{
                      transform: [{ translateY: internalPageTwoAnimation }],
                    }}
                    useNativeDriver
                  >
                    <TWCText
                      style={styles.sectionEnd}
                      testID={`${testIDPath.temperature}_page_two_hour_${index + 1}`}
                      variant={'condensedText36'}
                    >
                      {temperature}
                    </TWCText>
                    <TWCText
                      style={
                        ([styles.sectionEnd],
                        {
                          transform: [{ translateY: Theme.px(50) }],
                        })
                      }
                      testID={`${testIDPath.precipitation}_page_two_hour_${index + 1}`}
                      variant={'condensedText36'}
                    >
                      {precipChance}
                    </TWCText>
                  </Animated.View>
                </View>
              </View>
            );
          })}
        </View>
        <View style={[styles.page, { transform: [{ translateY: Theme.px(200) }] }]}>
          {page3.map((hour, index) => {
            const { timeLabel, iconCodeExtend, temperature, precipChance } = hour;

            return (
              <View
                key={timeLabel}
                style={[
                  styles.page,
                  {
                    left: Theme.px(125),
                  },
                ]}
              >
                <TWCText
                  style={styles.hourText}
                  testID={`${testIDPath.productLabel}_page_three_hour_${index + 1}`}
                  variant={'condensedText36'}
                >
                  {timeLabel}
                </TWCText>
                <View style={styles.animatedIcon}>
                  <TWCAnimatedIcon code={iconCodeExtend} />
                </View>
                <View>
                  <Animated.View
                    style={{
                      transform: [{ translateY: internalPageThreeAnimation }],
                    }}
                    useNativeDriver
                  >
                    <TWCText
                      style={styles.sectionEnd}
                      testID={`${testIDPath.temperature}_page_three_hour_${index + 1}`}
                      variant={'condensedText36'}
                    >
                      {temperature}
                    </TWCText>
                    <TWCText
                      style={
                        ([styles.sectionEnd],
                        {
                          transform: [{ translateY: Theme.px(50) }],
                        })
                      }
                      testID={`${testIDPath.precipitation}_page_three_hour_${index + 1}`}
                      variant={'condensedText36'}
                    >
                      {precipChance}
                    </TWCText>
                  </Animated.View>
                </View>
              </View>
            );
          })}
        </View>
      </Animated.View>
    </View>
  );
};

export const LDLHourly = {
  API: LDLHourlyAPI,
  UI: LDLHourlyUI,
};

const styles = StyleSheet.create({
  animatedIcon: {
    paddingRight: Theme.px(18),
  },
  hourText: {
    paddingLeft: Theme.px(18),
    paddingRight: Theme.px(18),
  },
  page: {
    flexDirection: 'row',
  },
  pageWrapper: {
    position: 'absolute',
  },
  productText: {
    paddingRight: Theme.px(48),
  },
  sectionEnd: {
    paddingRight: Theme.px(73),
  },
});
